// quoteFees.js — BM-H4496 band legal fee and additional fee pricing for the
// Quote Builder, read from the catalogue exactly as the backend sends it
// (hub/quoting_catalogue_v1.get_catalogue).
//
// Before BM-H4496 the builder read `catalogue.bands`, which the backend has
// never sent, so the band legal fee was £0 on every quote; and the Additional
// fees picker read `code` / `amount` where the catalogue carries `id` / `fee`,
// so every supplement was £0 and picking one marked them all picked.

const TXN_LABELS = {
  purchase: "Purchase",
  sale: "Sale",
  remortgage: "Remortgage",
  transfer_of_equity: "Transfer of equity",
};

const fmt = (n) => new Intl.NumberFormat("en-GB", {
  style: "currency", currency: "GBP", maximumFractionDigits: 0,
}).format(n);

function inBand(price, lower, upper) {
  return price >= (lower || 0) && (upper === null || upper === undefined || price <= upper);
}

// The band legal fee for a transaction, as { amount, label }, or null when
// the catalogue has no fee for it (a transfer of equity, or a purchase or
// sale under the £100,000 floor). Null is shown to the fee earner, never
// quietly priced at £0.
export function bandLegalFee(catalogue, transactionType, tenure, price) {
  const p = Number(price);
  if (!catalogue || !Number.isFinite(p) || p <= 0) return null;
  const txnLabel = TXN_LABELS[transactionType] || transactionType;

  if (transactionType === "remortgage") {
    const band = (catalogue.remortgage_bands || []).find(b => inBand(p, b.lower, b.upper));
    if (!band) return null;
    return { amount: Number(band.fee), label: `${txnLabel} legal fee (${band.label})` };
  }

  if (transactionType === "purchase" || transactionType === "sale") {
    const row = (catalogue.flat_fees || {})[`${tenure || "freehold"}_${transactionType}`];
    const bands = catalogue.price_bands || [];
    const idx = bands.findIndex(b => inBand(p, b.lower, b.upper));
    if (!row || idx < 0 || row.band_fees?.[idx] === undefined) return null;
    const b = bands[idx];
    const range = b.upper === null || b.upper === undefined
      ? `over ${fmt(b.lower - 1)}`
      : `${fmt(b.lower)} to ${fmt(b.upper)}`;
    return { amount: Number(row.band_fees[idx]), label: `${row.label} legal fee (${range})` };
  }

  return null;
}

// One catalogue additional fee as the picker and the quote line carry it.
// `fee` is a fixed price, `fee_from` a starting price the fee earner may
// raise, `fee_pct_of_base` a share of the band legal fee (abortive work).
export function additionalFeeOption(item, baseFee = 0) {
  let amount = 0;
  let isFrom = false;
  if (item.fee !== undefined) amount = Number(item.fee);
  else if (item.fee_from !== undefined) { amount = Number(item.fee_from); isFrom = true; }
  else if (item.fee_pct_of_base !== undefined) amount = Math.round((Number(baseFee) || 0) * item.fee_pct_of_base);
  return { code: item.id, label: item.label, description: item.label, amount, is_from: isFrom };
}

export function catalogueAdditionalFee(catalogue, id) {
  return (catalogue?.additional_fees || []).find(f => f.id === id) || null;
}
